import axiosInstance from "./axiosInstance";
import { log, LogLevel } from "utils/LogUtil";

/**
 * 데스크톱 인증 핸드오프 서비스 (계약 §3.3.3 — 웹 핸드오프)
 *
 * PhAI 약국 데스크톱 앱은 카카오와 직접 대화할 수 없어(카카오 Redirect URI가 HTTP/HTTPS만
 * 지원, 포트 와일드카드 불가) 웹이 인증을 대행하고 앱에 세션을 넘긴다.
 * 웹이 인증을 마친 뒤 이 API로 **일회용 코드**를 발급받아, 앱의 loopback 수신 서버(`cb`)로
 * 전달한다. 코드 자체는 세션이 아니며, 앱이 `/auth/desktop/exchange`로 교환해야 쿠키가 발급된다.
 *
 * ⚠️ 백엔드가 동시 구현 중이라 이 엔드포인트가 아직 없을 수 있다. 부재 시 호출은
 * 실패(404/5xx/네트워크)로 rejection되며, 호출부가 이를 잡아 화면에 에러 안내를 띄운다.
 */

/** 핸드오프 코드 발급 결과 */
export interface DesktopHandoffResult {
  /** 일회용 코드 (TTL 60초, 1회용 — 계약 S3). 화면 표시·앱 전달 용도로만 쓴다. */
  code: string;
}

/**
 * 일회용 핸드오프 코드 발급.
 *
 * 인증 필수 — 로그인/가입 후 세션 쿠키가 있는 상태에서만 성공한다.
 * `cb`(loopback 콜백 주소)를 함께 보내 서버가 재검증하도록 한다(웹·서버 이중 방어, 계약 S1).
 *
 * @param cb 앱이 넘긴 loopback 콜백 URL (`http://127.0.0.1:<port>/cb`)
 * @returns 발급된 일회용 코드
 * @throws 엔드포인트 부재/서버 오류/미인증 시 rejection (호출부가 처리)
 */
export const requestDesktopHandoff = async (cb: string): Promise<DesktopHandoffResult> => {
  const response = await axiosInstance.post("/api/v1/auth/desktop/handoff", { cb });

  // 이 repo의 응답 규약은 { error: 0, data: {...} } 형태다(authService·kakaoAuth 참조).
  // 백엔드가 동시 구현 중이므로 규약대로 파싱하되, 중첩/평탄 응답 모두 방어적으로 처리한다.
  const body = response?.data;

  if (body && typeof body === "object" && "error" in body && body.error) {
    throw new Error(
      typeof body.error === "string" ? body.error : "핸드오프 코드 발급에 실패했습니다."
    );
  }

  const payload =
    body && typeof body === "object" && "data" in body && body.data ? body.data : body;
  const code: unknown = payload?.code;

  if (typeof code !== "string" || code.length === 0) {
    log(LogLevel.ERROR, "requestDesktopHandoff: 응답에 code가 없습니다.");
    throw new Error("핸드오프 응답 형식이 올바르지 않습니다.");
  }

  // 규칙 4: 코드는 화면에만 표시한다 — 로그에 남기지 않는다(값 로깅 금지).
  return { code };
};
